// src/components/agents/AgentModelSelect.tsx
//
// Par provedor + modelo do agente (Reforma UX §6.6, AGT-02).
//
// - Inicializa com o modelo ATUAL do agente: usa `initial` quando o caller tem
//   o detalhe (AgentDetailModal → config.model_policy); sem `initial`
//   (AgentCard — AgentListItem não traz config), busca GET /v1/agents/:ref e
//   extrai config.model_policy. Sem policy → placeholder.
// - Trocar o provedor recarrega a lista de modelos e SELECIONA automaticamente
//   o 1º modelo como padrão (AGT-02), persistindo.
// - Persistência transacional: o estado da lista só é confirmado depois do
//   PATCH; qualquer erro restaura provider, modelo e fallbacks confirmados.

import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ChevronDown, GitBranch, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { ProviderSelect } from '@/components/llm/ProviderSelect';
import { ModelSelect } from '@/components/llm/ModelSelect';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  isValidModelFallbackOrder,
  parseModelPolicy,
  synchronizeModelPolicy,
  type ModelPolicyPair,
  type ModelPolicyValue,
} from './agent-constants';

interface AgentModelSelectProps {
  agentRef: string;
  initial?: ModelPolicyValue;
  /** Layout compacto pro card (sem labels). */
  compact?: boolean;
  onError?: (message: string) => void;
  onSaved?: (policy: ModelPolicyValue) => void;
}

export function AgentModelSelect({
  agentRef,
  initial,
  compact = false,
  onError,
  onSaved,
}: AgentModelSelectProps) {
  const parsedInitial = parseModelPolicy(initial ?? {});
  const [provider, setProvider] = useState(parsedInitial.provider ?? '');
  const [modelKey, setModelKey] = useState(parsedInitial.model_key ?? '');
  const [fallbackModels, setFallbackModels] = useState<ModelPolicyPair[]>(
    parsedInitial.fallback_models ?? [],
  );
  const [fallbackProvider, setFallbackProvider] = useState('');
  const [fallbackModelKey, setFallbackModelKey] = useState('');
  const [fallbackEditorOpen, setFallbackEditorOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChoosingProvider, setIsChoosingProvider] = useState(false);
  const [inlineError, setInlineError] = useState('');
  // Somente a policy confirmada pelo backend mora neste ref. O estado visual
  // pode exibir a tentativa enquanto o PATCH está pendente, mas sempre volta
  // para este snapshot em caso de erro.
  const committedPolicyRef = useRef<ModelPolicyValue>(parsedInitial);
  // Usuário já interagiu → fetch tardio do detalhe não sobrescreve a escolha.
  const touchedRef = useRef(false);
  // AGT-02: armado SÓ na troca de provedor pelo usuário — evita PATCH no mount.
  const autoSelectRef = useRef(false);
  const fallbackPanelId = useId();
  const fallbackHeadingId = useId();
  const fallbackValidationId = useId();
  const busy = isSaving || isChoosingProvider;

  // (a) Fallback: SÓ busca o detalhe quando o caller não sabe a policy
  // (initial === undefined — backend antigo sem model_policy na listagem).
  // AGT-04: `initial` presente (mesmo vazio = "sem modelo configurado") é
  // autoritativo — disparar GET /v1/agents/:slug por card era o N+1 que
  // segurava a tela de agentes em dezenas de segundos (detalhe custa 2-10s:
  // storage + executions count + tools).
  const hasInitial = initial !== undefined;
  const { data: detail } = useQuery({
    queryKey: ['agent-model-policy', agentRef],
    queryFn: () => api.getAgent(agentRef),
    staleTime: 5 * 60 * 1000,
    enabled: !hasInitial,
  });
  useEffect(() => {
    if (!detail || touchedRef.current) return;
    const policy = parseModelPolicy(detail.config?.model_policy);
    committedPolicyRef.current = policy;
    setProvider(policy.provider ?? '');
    setModelKey(policy.model_key ?? '');
    setFallbackModels(policy.fallback_models ?? []);
  }, [detail]);

  // (b) Modelos do provedor selecionado — mesma queryKey do ModelSelect
  // (cache compartilhado). Usado pro auto-select do 1º modelo na troca.
  const { data: modelsData, isError: modelsQueryFailed } = useQuery({
    queryKey: ['llm-models', provider || undefined, 'text'],
    queryFn: () => api.listLlmModels({ provider, modality: 'text', page_size: 100 }),
    staleTime: 5 * 60 * 1000,
    enabled: !!provider,
  });
  // O catálogo é a fonte de verdade do par explícito provider/modelo do
  // fallback. Não há inferência pela model_key e nenhum par é gravado antes de
  // constar como disponível para o provider escolhido.
  const { data: fallbackModelsData } = useQuery({
    queryKey: ['llm-models', fallbackProvider || undefined, 'text'],
    queryFn: () => api.listLlmModels({ provider: fallbackProvider, modality: 'text', page_size: 100 }),
    staleTime: 5 * 60 * 1000,
    enabled: !!fallbackProvider,
  });

  const applyPolicyToView = (policy: ModelPolicyValue) => {
    setProvider(policy.provider ?? '');
    setModelKey(policy.model_key ?? '');
    setFallbackModels(policy.fallback_models ?? []);
  };

  const reportError = (message: string) => {
    setInlineError(message);
    onError?.(message);
  };

  const persist = async (
    nextProvider: string,
    nextModel: string,
    nextFallbackModels = fallbackModels,
    forceFallbackModels = false,
  ): Promise<boolean> => {
    const previousPolicy = committedPolicyRef.current;
    const policy = synchronizeModelPolicy(
      previousPolicy,
      { provider: nextProvider, model_key: nextModel },
      nextFallbackModels,
      { forceFallbackModels },
    );
    setInlineError('');
    setIsSaving(true);
    applyPolicyToView(policy);
    try {
      await api.updateAgent(agentRef, { model_policy: policy as Record<string, unknown> });
    } catch (err) {
      applyPolicyToView(previousPolicy);
      setIsSaving(false);
      reportError(
        err instanceof Error
          ? `Modelo não persistido: ${err.message}`
          : 'Modelo não persistido.',
      );
      return false;
    }
    committedPolicyRef.current = policy;
    setIsSaving(false);
    onSaved?.(policy);
    return true;
  };

  // (b) AGT-02: provedor trocou → 1º modelo da lista vira padrão (e persiste).
  useEffect(() => {
    if (!autoSelectRef.current || !provider) return;
    if (modelsQueryFailed) {
      autoSelectRef.current = false;
      setIsChoosingProvider(false);
      applyPolicyToView(committedPolicyRef.current);
      reportError('Não foi possível carregar os modelos deste provider.');
      return;
    }
    if (!modelsData) return;
    autoSelectRef.current = false;
    const first = (modelsData.models ?? []).find(
      (m) => m.status === 'active' || m.status === 'available_via_oauth',
    );
    if (first) {
      setModelKey(first.model_key);
      setIsChoosingProvider(false);
      void persist(provider, first.model_key);
    } else {
      setIsChoosingProvider(false);
      applyPolicyToView(committedPolicyRef.current);
      reportError('Este provider não possui modelo de texto ativo.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelsData, modelsQueryFailed, provider]);

  const updateFallbackModels = (next: ModelPolicyPair[]) => {
    touchedRef.current = true;
    void persist(provider, modelKey, next, true);
  };

  const fallbackDraftError = (() => {
    // ERRO FANTASMA DURANTE O SALVAMENTO (2026-07-28).
    //
    // O dono relatou: ao adicionar um fallback, aparece "Este par
    // provider/modelo já está na cadeia" — e mesmo assim salva certo. O erro
    // pisca e some.
    //
    // Causa: `persist` é OTIMISTA. Ele chama `applyPolicyToView(policy)` ANTES
    // do `await api.updateAgent(...)`, então a cadeia exibida já contém o par
    // novo enquanto a requisição está em voo. Os selects do rascunho só são
    // limpos DEPOIS que o await resolve. Nessa janela a comparação abaixo acha
    // o par do rascunho dentro da cadeia e acusa duplicata — de um par que o
    // próprio usuário acabou de adicionar com sucesso.
    //
    // Enquanto `busy`, o rascunho não é uma intenção pendente do usuário: é o
    // resto do que está sendo gravado. Validar duplicata aí compara o item com
    // ele mesmo. O hint de ação já cobre esse estado com "Aguarde o salvamento
    // atual", que é a informação correta para o momento.
    if (busy) return '';
    if (!fallbackProvider || !fallbackModelKey) return '';
    if (fallbackProvider === provider && fallbackModelKey === modelKey) {
      return 'O fallback não pode repetir o modelo primário.';
    }
    if (fallbackModels.some(
      (item) => item.provider === fallbackProvider && item.model_key === fallbackModelKey,
    )) {
      return 'Este par provider/modelo já está na cadeia.';
    }
    return '';
  })();
  const fallbackActionHint = fallbackDraftError
    || (busy
      ? 'Aguarde o salvamento atual.'
      : !fallbackProvider
        ? 'Selecione o provider do fallback.'
        : !fallbackModelKey
          ? 'Selecione o modelo fallback.'
          : '');

  const addFallbackModel = async () => {
    if (!fallbackProvider || !fallbackModelKey) return;
    const catalogEntry = (fallbackModelsData?.models ?? []).find(
      (model) => model.model_key === fallbackModelKey
        && (model.status === 'active' || model.status === 'available_via_oauth'),
    );
    if (!catalogEntry) {
      reportError('Selecione um modelo válido do catálogo deste provider.');
      return;
    }
    if (fallbackDraftError) {
      reportError(fallbackDraftError);
      return;
    }

    const next = [...fallbackModels];
    let lastProviderIndex = -1;
    for (let index = next.length - 1; index >= 0; index -= 1) {
      if (next[index]?.provider === fallbackProvider) {
        lastProviderIndex = index;
        break;
      }
    }
    const insertAt = lastProviderIndex >= 0
      ? lastProviderIndex + 1
      : fallbackProvider === provider
        ? 0
        : next.length;
    next.splice(insertAt, 0, { provider: fallbackProvider, model_key: fallbackModelKey });
    touchedRef.current = true;
    if (await persist(provider, modelKey, next, true)) {
      setFallbackProvider('');
      setFallbackModelKey('');
    }
  };

  const movedFallbacks = (index: number, offset: -1 | 1): ModelPolicyPair[] | null => {
    const target = index + offset;
    if (target < 0 || target >= fallbackModels.length) return null;
    const next = [...fallbackModels];
    [next[index], next[target]] = [next[target]!, next[index]!];
    return isValidModelFallbackOrder(provider, next) ? next : null;
  };

  const fallbackEditor = (
    <>
      <div>
        <h3
          id={fallbackHeadingId}
          className={cn(
            'text-balance text-sm font-semibold text-raios-text',
            compact && 'sr-only',
          )}
        >
          Modelos fallback
        </h3>
        {!compact && (
          <p className="mt-1 text-pretty text-xs text-raios-muted">
            A ordem é preservada por blocos de provider e cada par é validado no catálogo.
          </p>
        )}
      </div>

      {fallbackModels.length > 0 ? (
        <ol className="space-y-2" aria-label="Ordem dos modelos fallback">
          {fallbackModels.map((item, index) => {
            const moveUp = movedFallbacks(index, -1);
            const moveDown = movedFallbacks(index, 1);
            return (
              <li
                key={`${item.provider}:${item.model_key}`}
                className={cn(
                  'flex min-w-0 items-center gap-2 rounded-md border border-raios-border',
                  compact ? 'px-2 py-1.5' : 'px-3 py-2',
                )}
              >
                <span className="tabular-nums text-xs text-raios-muted">{index + 1}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-raios-text">
                  <span className="text-raios-muted">{item.provider}</span> / {item.model_key}
                </span>
                <button
                  type="button"
                  aria-label={`Subir fallback ${index + 1}`}
                  disabled={!moveUp || busy}
                  onClick={() => moveUp && updateFallbackModels(moveUp)}
                  className="rounded p-1 text-raios-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 hover:text-raios-text disabled:opacity-40"
                >
                  <ArrowUp className="size-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={`Descer fallback ${index + 1}`}
                  disabled={!moveDown || busy}
                  onClick={() => moveDown && updateFallbackModels(moveDown)}
                  className="rounded p-1 text-raios-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 hover:text-raios-text disabled:opacity-40"
                >
                  <ArrowDown className="size-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={`Remover fallback ${index + 1}`}
                  disabled={busy}
                  onClick={() => updateFallbackModels(
                    fallbackModels.filter((_, current) => current !== index),
                  )}
                  className="rounded p-1 text-raios-danger-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 hover:bg-raios-danger/10 disabled:opacity-40"
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="text-pretty text-sm text-raios-muted">
          Sem fallback configurado. Escolha um provider e um modelo abaixo para criar a cadeia.
        </p>
      )}

      <div className={cn(
        'grid grid-cols-1 gap-3',
        !compact && 'md:grid-cols-[1fr_1fr_auto] md:items-end',
      )}>
        <ProviderSelect
          label="Provider do fallback"
          placeholder="Selecione o provider…"
          value={fallbackProvider}
          disabled={busy}
          onChange={(value) => {
            setFallbackProvider(value);
            setFallbackModelKey('');
            setInlineError('');
          }}
        />
        <ModelSelect
          label="Modelo fallback"
          placeholder="Selecione o modelo…"
          provider={fallbackProvider || undefined}
          value={fallbackModelKey}
          disabled={busy}
          onChange={(value) => {
            setFallbackModelKey(value);
            setInlineError('');
          }}
        />
        <Button
          type="button"
          variant="outline"
          className={compact ? 'w-full' : undefined}
          onClick={() => void addFallbackModel()}
          disabled={busy || !fallbackProvider || !fallbackModelKey || Boolean(fallbackDraftError)}
          aria-describedby={fallbackActionHint ? fallbackValidationId : undefined}
        >
          <Plus className="size-4" aria-hidden="true" />
          Adicionar
        </Button>
      </div>

      {fallbackActionHint && (
        <p
          id={fallbackValidationId}
          className={cn(
            'text-pretty text-xs',
            fallbackDraftError ? 'text-raios-danger-text' : 'sr-only',
          )}
          aria-live="polite"
        >
          {fallbackActionHint}
        </p>
      )}

      <p className="rounded-md bg-raios-elevated/40 px-3 py-2 text-pretty text-xs text-raios-text-secondary">
        Precedência: {provider && modelKey ? `${provider}/${modelKey}` : 'primário não definido'}
        {' → '}
        {fallbackModels.length
          ? fallbackModels.map((item) => `${item.provider}/${item.model_key}`).join(' → ')
          : 'sem fallback do agente'}.
      </p>
    </>
  );

  return (
    <div
      // compact = card da listagem → grid 2 col do wireframe (.agt-model).
      className={compact ? 'agt-model' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}
      onClick={(e) => e.stopPropagation()}
      aria-busy={busy}
    >
      <ProviderSelect
        label={compact ? '' : 'Provedor'}
        placeholder="Provedor…"
        value={provider}
        disabled={busy}
        onChange={(p) => {
          if (p === provider) return;
          touchedRef.current = true;
          setInlineError('');
          setProvider(p);
          // Provedor trocou → lista de modelos muda; limpa seleção e arma o
          // auto-select do 1º modelo (efeito acima, quando a lista carregar).
          setModelKey('');
          setIsChoosingProvider(true);
          autoSelectRef.current = true;
        }}
      />
      <ModelSelect
        label={compact ? '' : 'Modelo'}
        placeholder="Modelo…"
        provider={provider || undefined}
        value={modelKey}
        disabled={busy}
        onChange={(m) => {
          if (m === modelKey) return;
          touchedRef.current = true;
          autoSelectRef.current = false;
          void persist(provider, m);
        }}
      />
      {compact ? (
        <>
          <button
            type="button"
            className="col-span-2 flex min-w-0 items-center gap-2 rounded-md border border-raios-border bg-raios-elevated/30 px-2.5 py-2 text-left text-xs text-raios-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40"
            aria-expanded={fallbackEditorOpen}
            aria-controls={fallbackPanelId}
            onClick={() => setFallbackEditorOpen((open) => !open)}
          >
            <GitBranch className="size-4 shrink-0 text-raios-accent-text" aria-hidden="true" />
            <span className="shrink-0 font-medium">Fallbacks ({fallbackModels.length})</span>
            <span className="min-w-0 flex-1 truncate text-right text-raios-muted">
              {fallbackModels.length
                ? fallbackModels.map((item) => `${item.provider}/${item.model_key}`).join(' → ')
                : 'Configurar cadeia'}
            </span>
            <ChevronDown
              className={cn('size-4 shrink-0 text-raios-muted', fallbackEditorOpen && 'rotate-180')}
              aria-hidden="true"
            />
          </button>
          {fallbackEditorOpen && (
            <section
              id={fallbackPanelId}
              className="col-span-2 min-w-0 space-y-3 rounded-md border border-raios-border bg-raios-card p-3"
              aria-labelledby={fallbackHeadingId}
            >
              {fallbackEditor}
            </section>
          )}
        </>
      ) : (
        <section
          id={fallbackPanelId}
          className="space-y-4 rounded-lg border border-raios-border bg-raios-card p-4 md:col-span-2"
          aria-labelledby={fallbackHeadingId}
        >
          {fallbackEditor}
        </section>
      )}
      {isSaving && (
        <p
          className={cn(
            'text-pretty text-xs text-raios-muted',
            compact ? 'col-span-2' : 'md:col-span-2',
          )}
          role="status"
        >
          Salvando configuração do modelo…
        </p>
      )}
      {inlineError && (
        <p
          className={cn(
            'text-pretty text-xs text-raios-danger-text',
            compact ? 'col-span-2' : 'md:col-span-2',
          )}
          role="alert"
        >
          {inlineError}
        </p>
      )}
    </div>
  );
}
